import { useContext, useRef } from 'react';

import i18n, { i18nNS } from '../../../../i18n';
import { LayoutContext } from '../../Context';

export interface UseExpandCollapseToggleButton {
  /** @default 'comments' */
  type?: 'comments' | 'faq';
}

const useExpandCollapseToggleButton = ({ type = 'comments' }: UseExpandCollapseToggleButton) => {
  const { setIsCommentsPanelOpen, isCommentsPanelExpanded, setIsCommentsPanelExpanded } =
    useContext(LayoutContext);

  const expandButtonRef = useRef<HTMLButtonElement>(null);

  const toggleExpand = () => {
    setIsCommentsPanelExpanded((prevVal) => !prevVal);
    // waiting for drawer shrinking animation
    // NOTE 1: do not use async/await as it disables button till settimeout. After timeout, there will be a race between enabling button and setting focus. In almost all cases focus wins the race, so it tries to focus the button before enabling it.
    // NOTE 2: do not return this promise as it will disable the button till settimeout is executed and we will get same race condition as previous note.
    new Promise((resolve) => setTimeout(resolve, 0)).then(() => {
      expandButtonRef.current?.focus();
    });
  };

  const handleClose = () => {
    setIsCommentsPanelOpen(false);
    setIsCommentsPanelExpanded(false);
  };

  const getAriaLabel = (device: 'desktop' | 'mobile') => {
    switch (device) {
      case 'mobile':
        switch (type) {
          case 'faq':
            return 'click_to_hide_frequently_asked_questions_panel';
          case 'comments':
          default:
            return 'click_to_hide_discussion_panel';
        }
      case 'desktop':
      default:
        switch (type) {
          case 'faq':
            return isCommentsPanelExpanded
              ? 'click_to_collapse_frequently_asked_questions_panel'
              : 'click_to_expand_frequently_asked_questions_panel';
          case 'comments':
          default:
            return isCommentsPanelExpanded
              ? 'click_to_collapse_comments_panel'
              : 'click_to_expand_comments_panel';
        }
    }
  };

  const getTranslatedAriaLabel = (device: 'desktop' | 'mobile') => {
    return i18n.t(getAriaLabel(device), { ns: i18nNS.SHARED });
  };

  return { expandButtonRef, isCommentsPanelExpanded, toggleExpand, handleClose, getTranslatedAriaLabel };
};

export default useExpandCollapseToggleButton;
